import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, Eye } from "@phosphor-icons/react";
import { RSpinBlock } from "@/components/RSpin";

/** Format publisher default split as "80/20" (pub / platform). */
function formatSplitRatio(pubPct) {
  const pub = Number(pubPct);
  const resolved = Number.isFinite(pub) && pub > 0 ? pub : 100;
  const platform = Math.max(0, Math.round((100 - resolved) * 100) / 100);
  const pubRounded = Math.round(resolved * 100) / 100;
  return `${pubRounded}/${platform}`;
}

export default function AdminPublishers() {
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const didFetch = useRef(false);

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    (async () => {
      try {
        const { data } = await API.get("/admin/publishers");
        setPublishers(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = publishers.filter((p) => {
    const hay = `${p.company_name || ""} ${p.email || ""} ${p.sub_id || ""} ${p.status || ""} ${formatSplitRatio(p.default_split_pub_pct)}`.toLowerCase();
    return hay.includes(search.toLowerCase());
  });

  return (
    <div className="p-8" data-testid="admin-publishers-page">
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
          Network / Partners
        </p>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">Publishers</h1>
        <p className="text-sm text-slate-500 mt-2">{publishers.length} registered publishers</p>
      </div>

      {/* Pending-approval banner — deep-links into each pub's detail page */}
      {(() => {
        const pending = publishers.filter((p) => (p.status || "active") === "pending_approval");
        if (pending.length === 0) return null;
        return (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between" data-testid="pending-approvals-banner">
            <div>
              <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-amber-700 mb-0.5">
                Pending approval
              </div>
              <div className="text-sm text-amber-900">
                <b>{pending.length}</b> publisher{pending.length === 1 ? "" : "s"} waiting for your review.
              </div>
            </div>
            <button
              onClick={() => setSearch("pending_approval")}
              className="text-[12px] font-semibold text-amber-700 hover:text-amber-900 underline"
            >
              Filter list
            </button>
          </div>
        );
      })()}

      <div className="relative mb-4">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search by company, email, or SubID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-10"
          data-testid="publishers-search-input"
        />
      </div>

      {loading ? (
        <RSpinBlock label="Loading publishers" height={320} data-testid="publishers-spinner" />
      ) : (
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold">Company</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold">Contact</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold">Email</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold">SubID</th>
                <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold">Split</th>
                <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold">Status</th>
                <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold">Brands</th>
                <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold">Reports</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate(`/admin/publishers/${p.id}`)} data-testid={`publisher-row-${p.sub_id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{p.company_name}</td>
                  <td className="px-4 py-3 text-slate-600">{p.first_name} {p.last_name}</td>
                  <td className="px-4 py-3 text-slate-600">{p.email}</td>
                  <td className="px-4 py-3 font-mono text-xs font-bold text-[#002FA7]">{p.sub_id}</td>
                  <td className="px-4 py-3 text-center">
                    {(() => {
                      const pubPct = Number(p.default_split_pub_pct);
                      const resolved = Number.isFinite(pubPct) && pubPct > 0 ? pubPct : 100;
                      const isPassthrough = Math.abs(resolved - 100) < 1e-9;
                      return (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono font-semibold tabular-nums ${
                            isPassthrough
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-slate-100 text-slate-700"
                          }`}
                          title={`Publisher ${resolved}% / platform ${Math.max(0, 100 - resolved)}%`}
                          data-testid={`publisher-split-${p.sub_id}`}
                        >
                          {formatSplitRatio(resolved)}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {(() => {
                      const st = (p.status || "active").toLowerCase();
                      const pill = {
                        active:            { cls: "bg-emerald-50 text-emerald-700", label: "Active" },
                        paused:            { cls: "bg-amber-50 text-amber-700",     label: "Paused" },
                        pending_approval:  { cls: "bg-blue-50 text-blue-700",       label: "Pending" },
                        rejected:          { cls: "bg-red-50 text-red-700",         label: "Rejected" },
                      }[st] || { cls: "bg-slate-100 text-slate-700", label: st };
                      return (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${pill.cls}`}>
                          {pill.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-center font-mono">{p.assigned_brands_count}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      p.reports_published ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                    }`}>
                      {p.reports_published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Eye size={16} className="text-slate-400 hover:text-[#002FA7] transition-colors inline" />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400 text-sm">No publishers found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
